export interface ClientDiagnosticEventTypeOption {
  value: string;
  label: string;
}

/** Informational kiosk events — excluded from error-rate metrics and the main error list. */
export const LABELLED_DIAGNOSTIC_EVENT_TYPES = ['expected_rejection', 'scan_unmatched'];

/** Filter dropdown + human-readable labels for kiosk-reported client diagnostic events. */
export const CLIENT_DIAGNOSTIC_EVENT_TYPE_OPTIONS: ClientDiagnosticEventTypeOption[] = [
  { value: '', label: 'All event types' },
  { value: 'charged_no_order', label: 'Charged, no order' },
  { value: 'dart_exception', label: 'Dart exception' },
  { value: 'js_exception', label: 'JS exception' },
  { value: 'boot_failure', label: 'Boot failure' },
  { value: 'render_error', label: 'Render error' },
  { value: 'operation_slow', label: 'Operation slow' },
  { value: 'operation_hung', label: 'Operation hung' },
  { value: 'api_failure', label: 'API failure' },
  { value: 'cart_recreate', label: 'Cart recreate' },
  { value: 'duplicate_guest_blocked', label: 'Duplicate guest blocked' },
  { value: 'provider_error', label: 'Provider error' },
  { value: 'perf_sample', label: 'Perf sample' },
  { value: 'session_baseline', label: 'Session baseline' },
  { value: 'heartbeat', label: 'Heartbeat' },
  { value: 'expected_rejection', label: 'Expected rejection (labelled)' },
  { value: 'scan_unmatched', label: 'Unrecognised scan (labelled)' }
];

const EVENT_TYPE_LABELS = new Map(
  CLIENT_DIAGNOSTIC_EVENT_TYPE_OPTIONS
    .filter(opt => opt.value)
    .map(opt => [opt.value, opt.label])
);

/** Kiosk-reported event: card approved but no order was saved (financial impact). */
export const CHARGED_NO_ORDER_EVENT_TYPE = 'charged_no_order';

function formatScanKind(scanKind: string | undefined | null): string {
  switch (scanKind) {
    case 'packaging_qr':
      return 'packaging QR';
    case 'scanner_prefix':
      return 'scanner prefix';
    case 'unknown_ean':
      return 'unknown EAN';
    case 'other':
      return 'other';
    default:
      return scanKind || 'unknown scan';
  }
}

export function isLabelledDiagnosticEvent(event: any): boolean {
  return LABELLED_DIAGNOSTIC_EVENT_TYPES.includes(event?.eventType);
}

export function getLabelledDiagnosticEventLabel(event: any): string {
  const ctx = event?.context || {};

  if (event?.eventType === 'expected_rejection') {
    const errorCode = ctx.errorCode || 'unknown';
    return `Expected rejection — ${errorCode}`;
  }

  if (event?.eventType === 'scan_unmatched') {
    const scanKindLabel = formatScanKind(ctx.scanKind);
    const recovered = ctx.recoveredWithin60s === true;
    return `Unrecognised scan — ${scanKindLabel} — ${recovered ? 'recovered' : 'not recovered'}`;
  }

  return getClientDiagnosticEventTypeLabel(event?.eventType);
}

export function getClientDiagnosticEventTypeLabel(eventType: string | undefined | null): string {
  if (!eventType) return '—';
  return EVENT_TYPE_LABELS.get(eventType) ?? eventType;
}

export function isChargedNoOrderEvent(eventType: string | undefined | null): boolean {
  return eventType === CHARGED_NO_ORDER_EVENT_TYPE;
}

export function isClientDiagnosticErrorEvent(event: any): boolean {
  if (isLabelledDiagnosticEvent(event)) {
    return false;
  }

  return event?.level === 'error' ||
    [
      'dart_exception',
      'js_exception',
      'boot_failure',
      'render_error',
      'api_failure',
      'provider_error',
      CHARGED_NO_ORDER_EVENT_TYPE
    ].includes(event?.eventType);
}

export function getClientDiagnosticEventTypeBadgeClass(eventType: string | undefined | null): string {
  if (isChargedNoOrderEvent(eventType)) {
    return 'bg-danger financial-critical-badge';
  }
  if (eventType === 'expected_rejection' || eventType === 'scan_unmatched') {
    return 'bg-info text-dark labelled-event-badge';
  }
  return 'bg-secondary';
}
